// Stellar-mass black hole: a Schwarzschild geodesic raymarcher.
//
// By default it is lensing only (LEGACY_FRAGMENT, unchanged): the black
// silhouette and the bent stars. The accretion disc below is parked behind
// a flag, off by default: ?bhdisc=1, or createBlackHole({ disc: true }).
// See "The stellar black hole's disc (parked)" in docs/REALISM.md.
//
// With the disc on:
//
// One shader traces every pixel's light ray backwards through the
// Schwarzschild metric with the Binet equation
//
//     d²u/dφ² = -u + (3/2) u²        u = r_s / r
//
// RK4 in φ, in the ray's own orbital plane (the geodesic stays in the plane
// through the hole, the camera and the ray). The plane of the disc cuts that
// orbital plane along one line, so the ray crosses the disc plane at fixed
// φ = δ + π/2 + kπ, found exactly: each crossing inside ISCO..outer edge adds
// the disc's emission there, front to back, with the disc's own transparency.
// That one rule gives every image of the disc at once: the near side in front
// of the shadow, the far side lensed up over the top and down under the
// bottom (the primary image), the thin secondary ring hugging the shadow
// (n = 1) and the photon ring inside it (n = 2). What escapes samples the sky
// (lensed stars, and the Milky Way when it stands behind the hole).
//
// The disc: a Novikov-Thorne / Shakura-Sunyaev thin disc from the ISCO
// (3 r_s) outward, T ∝ r^-3/4 (1 - sqrt(r_in/r))^1/4, emitting as a
// blackbody. Each crossing knows the photon's angular momentum about the
// disc axis, so the frequency shift g = ν_obs/ν_em (Doppler from the
// Keplerian orbit, gravitational redshift, the camera's own blueshift) is
// exact, and a blackbody seen through g is a blackbody at g·T (I_ν/ν³ is
// invariant). By default the shift is turned almost all the way down, as
// James, von Tunzelmann, Franklin & Thorne (2015) did for Gargantua: `shift`
// scales log g, 0 is the film's symmetric gold disc, 1 is fully physical
// (beaming and colour shift). ?bhdoppler=1 (or setDopplerStrength) shows the
// physical one.
//
// Turbulence: streaks along the flow from fBm in disc-local log-polar space,
// carried round at the Keplerian rate (Ω ∝ r^-3/2) and so sheared. Two
// layers of finite life cross-fade (variance-preserving), so the shear never
// winds the pattern into aliasing, and each octave fades out where a pixel's
// footprint on the disc (from screen derivatives of the crossing radius and
// azimuth) would no longer resolve it, which keeps it free of shimmer.
//
// Constants describe a ~100 M☉ stellar-mass hole; visual proportions in the
// scene come from visualRs.
import * as THREE from 'three';
import { placeholderSkyCube } from './sky/skyCube.js';
import { OKLAB_MIX_GLSL } from './shaders/oklab.js';

const STELLAR_BH = {
  massSolar: 100,
  schwarzschildRadiusKm: 295,         // 2GM/c² for 100 M☉
  spinParameter: 0,                   // a/M, dimensionless: the shader traces the Schwarzschild (non-rotating) metric
  isconRsRatio: 4.23,                 // ISCO / r_s for prograde at a=0.5
};

// Disc defaults (radii in r_s, temperatures in kelvin). TEMPERATURE is the
// profile's peak in the gas's own frame (at r = 49/36 r_in); see the colour
// note in docs/REALISM.md for why it is a few thousand kelvin.
const DISC = {
  inner: 3,
  outer: 15,
  peakK: 4500,
  exposure: 1.7,
  // Optical depth of the disc seen face-on at mean density.
  tau: 0.9,
  // Fraction of log g kept: 0 the film's symmetric disc, 1 fully physical.
  shift: 0.12,
  // OKLCh chroma kept in the disc's colour (1 = the blackbody's own).
  chroma: 1.0,
  // Highlight shoulder on the disc's radiance (see the shader).
  kneeLo: 0.55,
  kneeHi: 1.6,
  // With a backdrop (the Milky Way), the disc's tilt from edge-on as seen
  // from the side opposite the galaxy, degrees.
  tipDeg: 4,
  // The inner edge's orbit, seconds on the screen. The real one at 100 M☉
  // takes 45 ms; the ratio between radii stays Keplerian.
  innerPeriodS: 36,
};

// Blackbody table: linear sRGB (D65, a 6500 K Planckian rendered white) per
// kelvin, log-spaced. RGB holds the chromaticity scaled to unit luminance, A
// holds log2 of the luminance relative to T_REF.
const BB_SIZE = 256, BB_T0 = 800, BB_T1 = 40000, BB_T_REF = 6500;

// The march: RK4 steps in φ and the angle they cover (1.5 turns, enough for
// the primary, n = 1 and n = 2 images).
const MARCH_STEPS = 220, MARCH_PHI = 3 * Math.PI;
// A headset's march (XR_MARCH, a Quest 3 at 72 Hz): the same sweep in fewer, longer steps.
const XR_MARCH_STEPS = 110;

function cie1931(lambda) {
  // Wyman, Sloan & Shirley (2013) multi-lobe fit of the CIE 1931 2° observer.
  const g = (x, mu, s1, s2) => { const t = (x - mu) / (x < mu ? s1 : s2); return Math.exp(-0.5 * t * t); };
  return [
    1.056 * g(lambda, 599.8, 37.9, 31.0) + 0.362 * g(lambda, 442.0, 16.0, 26.7) - 0.065 * g(lambda, 501.1, 20.4, 26.2),
    0.821 * g(lambda, 568.8, 46.9, 40.5) + 0.286 * g(lambda, 530.9, 16.3, 31.1),
    1.217 * g(lambda, 437.0, 11.8, 36.0) + 0.681 * g(lambda, 459.0, 26.0, 13.8),
  ];
}

function blackbodyLinearSrgb(T) {
  let X = 0, Y = 0, Z = 0;
  for (let l = 380; l <= 780; l += 5) {
    const b = 1 / (Math.pow(l * 1e-9, 5) * (Math.exp(1.4387769e7 / (l * T)) - 1));
    const [x, y, z] = cie1931(l);
    X += b * x; Y += b * y; Z += b * z;
  }
  return [
    3.2406 * X - 1.5372 * Y - 0.4986 * Z,
    -0.9689 * X + 1.8758 * Y + 0.0415 * Z,
    0.0557 * X - 0.2040 * Y + 1.0570 * Z,
  ];
}

function createBlackbodyTexture() {
  const white = blackbodyLinearSrgb(BB_T_REF);
  const lumOf = (c) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  const refLum = lumOf(white.map((v, i) => v / white[i]));
  const data = new Float32Array(BB_SIZE * 4);
  for (let i = 0; i < BB_SIZE; i++) {
    const T = BB_T0 * Math.pow(BB_T1 / BB_T0, i / (BB_SIZE - 1));
    // White-balance to the 6500 K Planckian (von Kries in linear RGB) and
    // scale so its luminance is 1: luminance then reads as radiance in the
    // visible band relative to 6500 K.
    const c = blackbodyLinearSrgb(T).map((v, k) => Math.max(0, v / white[k]));
    const lum = Math.max(lumOf(c), 1e-30);
    data[i * 4] = c[0] / lum;
    data[i * 4 + 1] = c[1] / lum;
    data[i * 4 + 2] = c[2] / lum;
    data[i * 4 + 3] = Math.log2(lum / refLum);
  }
  const tex = new THREE.DataTexture(data, BB_SIZE, 1, THREE.RGBAFormat, THREE.FloatType);
  // Half floats filter linearly everywhere; float32 linear filtering is an
  // extension. Converted on upload by three.
  const half = new Uint16Array(data.length);
  for (let i = 0; i < data.length; i++) half[i] = THREE.DataUtils.toHalfFloat(data[i]);
  tex.image.data = half;
  tex.type = THREE.HalfFloatType;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  // Luminance of a blackbody at T relative to the table's reference, for the
  // exposure's normalisation on the CPU.
  tex.userData.relLum = (T) => {
    const c = blackbodyLinearSrgb(T).map((v, k) => Math.max(0, v / white[k]));
    return lumOf(c) / refLum;
  };
  return tex;
}

const LEGACY_FRAGMENT = /* glsl */`
  ${OKLAB_MIX_GLSL}
  uniform samplerCube uSkybox;  // the sky as drawn (src/sky/skyCube.js)
  uniform vec3 uBhWorldPos;
  uniform float uRs;
  uniform float uOpacity;
  uniform float uTime;

  varying vec3 vWorldPos;

  const float PI = 3.14159265358979;
  const int N_STEPS = 220;
  const float PHI_TOTAL = 3.0 * PI;
  const float DPHI = PHI_TOTAL / float(N_STEPS);

  // Schwarzschild Binet acceleration. u = r_s / r (dimensionless).
  float geodesicAccel(float u) {
    return -u + 1.5 * u * u;
  }

  // RK4 step of the second-order ODE (u, du/dφ).
  void integrateRK4(inout float u, inout float du, float h) {
    float k1u = du;
    float k1d = geodesicAccel(u);
    float k2u = du + 0.5 * h * k1d;
    float k2d = geodesicAccel(u + 0.5 * h * k1u);
    float k3u = du + 0.5 * h * k2d;
    float k3d = geodesicAccel(u + 0.5 * h * k2u);
    float k4u = du + h * k3d;
    float k4d = geodesicAccel(u + h * k3u);
    u  += h / 6.0 * (k1u + 2.0 * k2u + 2.0 * k3u + k4u);
    du += h / 6.0 * (k1d + 2.0 * k2d + 2.0 * k3d + k4d);
  }

  vec3 sampleSkybox(vec3 dir) {
    vec3 d = normalize(dir);
    // The sky exactly as drawn round it (src/sky/skyCube.js), so where the
    // bending fades out at the quad's edge the lensed sky meets the real
    // one without a seam. Lensing itself piles the stars into the arcs.
    return textureCube(uSkybox, d).rgb;
  }

  void main() {
    vec3 viewDir = normalize(vWorldPos - cameraPosition);
    vec3 camLocal = cameraPosition - uBhWorldPos;

    // BH behind the camera — let the underlying scene show through.
    if (dot(-camLocal, viewDir) < 0.0) {
      discard;
    }

    float r0 = length(camLocal);
    vec3 eR = camLocal / r0;
    vec3 perp = viewDir - dot(viewDir, eR) * eR;
    float perpLen = length(perp);
    if (perpLen < 1e-5) {
      // Radial ray straight at BH = certain capture.
      gl_FragColor = vec4(0.0, 0.0, 0.0, uOpacity);
      return;
    }
    vec3 ePhi = perp / perpLen;

    float v_r = dot(viewDir, eR);
    float v_phi = perpLen;

    // Impact parameter at the camera. Geometric — used as a fast cull
    // for rays whose deflection is too small to matter visually.
    float bRs = (r0 * v_phi) / uRs;
    if (bRs > 400.0) {
      // Deflection α = 2/b (in r_s units) < 0.005 rad ≈ 0.3°. Below
      // visible threshold; skip integration and let the underlying
      // starfield show through unmodified. Wide enough that visible
      // lensing of distant background features (like the Milky Way
      // panorama) is captured for typical viewing distances.
      discard;
    }

    // Initial state at φ = 0 (camera position).
    float u = uRs / r0;
    float du = -u * v_r / v_phi;
    float phi = 0.0;

    bool captured = false;

    // Integrate until either the photon crosses the event horizon, or
    // it escapes (u < 0.005 and decreasing). Linear interpolation at
    // the escape boundary makes the exit phi a continuous function of
    // b — adjacent rays exit at nearly the same interpolated phi
    // instead of jumping by integer step boundaries, which is what
    // produced the visible concentric-ring banding before.
    for (int i = 0; i < N_STEPS; i++) {
      float prevU = u;
      float prevDu = du;
      float prevPhi = phi;
      integrateRK4(u, du, DPHI);
      phi += DPHI;

      if (u >= 1.0) {
        captured = true;
        break;
      }

      if (u < 0.005 && du < 0.0 && i > 5) {
        float frac = (prevU - 0.005) / max(prevU - u, 1e-6);
        u   = mix(prevU,  u,  frac);
        du  = mix(prevDu, du, frac);
        phi = prevPhi + frac * DPHI;
        break;
      }
    }

    if (captured) {
      gl_FragColor = vec4(0.0, 0.0, 0.0, uOpacity);
      return;
    }

    // Compute final direction from photon's velocity at integration end.
    float dr_dphi = -uRs / max(u * u, 1e-8) * du;
    float rEnd = uRs / max(u, 1e-8);
    vec3 finalRadialDir = cos(phi) * eR + sin(phi) * ePhi;
    vec3 finalTangentialDir = -sin(phi) * eR + cos(phi) * ePhi;
    vec3 vel = dr_dphi * finalRadialDir + rEnd * finalTangentialDir;
    vec3 finalDir = normalize(vel);

    // For rays still close to the BH at the end of integration (multi-
    // orbit trajectories that didn't fully escape inside our phi window),
    // smoothly fade toward black. Replaces the hard u > 0.15 cutoff that
    // created a visible discontinuous ring just outside the silhouette.
    float trapWeight = smoothstep(0.05, 0.4, u);
    vec3 sky = sampleSkybox(finalDir);

    // Photon ring brightening. Rays with impact parameter just outside
    // b_crit = 3√3/2 r_s ≈ 2.598 orbit the BH multiple times, sampling
    // sky brightness on each pass before escaping. The accumulated
    // brightness produces a thin bright ring at the silhouette edge,
    // exactly what real BH images (EHT M87*, Sgr A*) show. The
    // exponential profile localizes it to a narrow band — Bardeen
    // showed the ring widths shrink as exp(-π) per winding, so a
    // fast-decay exponential captures the dominant first-order ring.
    float ringDist = max(bRs - 2.598, 0.001);
    float photonRingBoost = 1.0 + 2.4 * exp(-ringDist * 5.5);

    // Two-stage chromatic capture path. Real light climbing out of a
    // Schwarzschild potential well undergoes gravitational redshift —
    // the apparent frequency drops, so the source spectrum shifts
    // toward red as the ray's emission point approaches the horizon.
    // For multi-orbit trapped trajectories that didn't quite escape,
    // we model this perceptually:
    //   redshiftPhase (0 → 1 across trapWeight 0..0.5): OKLCh blend of
    //     the lensed sky toward a deep crimson via the perceptual hue
    //     arc. sRGB mix through the same range washes through muddy
    //     grey-brown at the midpoint, which kills the dramatic red
    //     limb glow.
    //   capturePhase (0 → 1 across trapWeight 0.4..1.0): OKLCh blend
    //     of the redshifted colour toward black. OKLCh holds chroma
    //     longer than linear lerp, so the colour stays vivid red
    //     right up until the event horizon swallows it — the way
    //     EHT-class BH images show a glowing rim against the
    //     silhouette rather than a dim grey gradient.
    vec3 lensedSky = sky * photonRingBoost;
    float redshiftPhase = smoothstep(0.0, 0.5, trapWeight);
    float capturePhase = smoothstep(0.4, 1.0, trapWeight);
    vec3 redshiftTint = vec3(0.55, 0.10, 0.02);
    vec3 redshifted = oklchMix(lensedSky, redshiftTint, redshiftPhase);
    vec3 finalColor = oklchMix(redshifted, vec3(0.0), capturePhase);

    // Alpha based on impact parameter — b is a smooth function of
    // screen position, so this fade has no source of banding. Near
    // b_crit the lensing is at full strength; by 300 r_s the deflection
    // is too small to matter and we let the underlying starfield show.
    float lensAlpha = 1.0 - smoothstep(50.0, 300.0, bRs);

    gl_FragColor = vec4(finalColor, lensAlpha * uOpacity);
  }
`;

// The march with the disc (the metric is drawn as Schwarzschild, so the
// disc's inner edge is the a = 0 ISCO, 3 r_s).
const DISC_FRAGMENT = /* glsl */`
  ${OKLAB_MIX_GLSL}
  uniform samplerCube uSkybox;  // the sky as drawn (src/sky/skyCube.js)
  uniform sampler2D uBlackbody;
  uniform vec3 uBhWorldPos;
  uniform float uRs;
  uniform float uOpacity;
  uniform float uTime;

  // Disc frame (world axes): normal and two in-plane axes, e1 x e2 = n.
  uniform vec3 uDiscN;
  uniform vec3 uDiscE1;
  uniform vec3 uDiscE2;
  uniform float uDiscIn;      // r_s
  uniform float uDiscOut;     // r_s
  uniform float uDiscPeakK;   // K, gas frame
  uniform float uDiscExposure;// radiance at the peak, already / L(peak)
  uniform float uDiscTau;
  uniform float uDiscShift;
  uniform float uDiscChroma;
  uniform float uDiscOn;
  uniform float uKneeLo;
  uniform float uKneeHi;
  uniform float uOmegaIn;     // rad per second at the inner edge
  // Turbulence layers: x age (s), y seed, z weight.
  uniform vec3 uLayerA;
  uniform vec3 uLayerB;
  // The Milky Way as seen from the hole (rendered now and then into
  // uBackdrop by a camera at the hole): projection x view rotation.
  uniform sampler2D uBackdrop;
  uniform mat4 uBackdropView;
  uniform float uBackdropOn;

  varying vec3 vWorldPos;

  const float PI = 3.14159265358979;
#ifdef XR_MARCH
  const int N_STEPS = ${XR_MARCH_STEPS};
#else
  const int N_STEPS = ${MARCH_STEPS};
#endif
  const float PHI_TOTAL = ${MARCH_PHI.toFixed(9)};
  const float DPHI = PHI_TOTAL / float(N_STEPS);
  const float BB_LOG_T0 = ${Math.log(BB_T0).toFixed(6)};
  const float BB_LOG_T1 = ${Math.log(BB_T1).toFixed(6)};
  const float BB_N = ${BB_SIZE.toFixed(1)};
  // Noise frequencies in disc space: per radian of azimuth and per unit of
  // ln r. Their ratio is the streaks' length along the flow over their width.
  const float AZ_FREQ = 1.5;
  const float RAD_FREQ = 21.0;
  const float RING_FREQ = 26.0;
  // The temperature profile's peak, x^-3/4 (1 - sqrt(3/x))^1/4 at x = 49/12.
  const float F_MAX = 0.214226;

  float geodesicAccel(float u) {
    return -u + 1.5 * u * u;
  }

  void integrateRK4(inout float u, inout float du, float h) {
    float k1u = du;
    float k1d = geodesicAccel(u);
    float k2u = du + 0.5 * h * k1d;
    float k2d = geodesicAccel(u + 0.5 * h * k1u);
    float k3u = du + 0.5 * h * k2d;
    float k3d = geodesicAccel(u + 0.5 * h * k2u);
    float k4u = du + h * k3d;
    float k4d = geodesicAccel(u + h * k3u);
    u  += h / 6.0 * (k1u + 2.0 * k2u + 2.0 * k3u + k4u);
    du += h / 6.0 * (k1d + 2.0 * k2d + 2.0 * k3d + k4d);
  }

  vec3 sampleSkybox(vec3 dir) {
    vec3 d = normalize(dir);
    // The sky exactly as drawn round it (src/sky/skyCube.js), so where the
    // bending fades out at the quad's edge the lensed sky meets the real
    // one without a seam. Lensing itself piles the stars into the arcs.
    return textureCube(uSkybox, d).rgb;
  }

  // The galaxy behind the hole, by the ray's final direction.
  vec3 sampleBackdrop(vec3 dir) {
    vec4 c = uBackdropView * vec4(dir, 0.0);
    if (c.w <= 1e-6 || uBackdropOn < 0.5) return vec3(0.0);
    vec2 uv = c.xy / c.w * 0.5 + 0.5;
    vec2 edge = smoothstep(vec2(0.0), vec2(0.06), uv) * smoothstep(vec2(1.0), vec2(0.94), uv);
    return texture2D(uBackdrop, clamp(uv, 0.0, 1.0)).rgb * edge.x * edge.y;
  }

  // Blackbody radiance in the visible band, linear sRGB, relative to the
  // table's 6500 K reference.
  vec3 blackbody(float T) {
    float s = clamp((log(max(T, 1.0)) - BB_LOG_T0) / (BB_LOG_T1 - BB_LOG_T0), 0.0, 1.0);
    vec4 v = texture2D(uBlackbody, vec2((s * (BB_N - 1.0) + 0.5) / BB_N, 0.5));
    return v.rgb * exp2(v.a);
  }

  float hash13(vec3 p3) {
    p3 = fract(p3 * 0.1031);
    p3 += dot(p3, p3.zyx + 31.32);
    return fract((p3.x + p3.y) * p3.z);
  }

  // Value noise, quintic fade, in [-1, 1].
  float vnoise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 w = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
    float n000 = hash13(i);
    float n100 = hash13(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash13(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash13(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash13(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash13(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash13(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash13(i + vec3(1.0, 1.0, 1.0));
    float x00 = mix(n000, n100, w.x);
    float x10 = mix(n010, n110, w.x);
    float x01 = mix(n001, n101, w.x);
    float x11 = mix(n011, n111, w.x);
    return mix(mix(x00, x10, w.y), mix(x01, x11, w.y), w.z) * 2.0 - 1.0;
  }

  // fBm whose octaves fade out once a pixel's footprint (fp, in noise units
  // at the base octave) passes half their wavelength, so unresolved detail
  // goes to the mean instead of aliasing.
  float discFbm(vec3 q, float fp) {
    float sum = 0.0;
    float amp = 0.5;
    float f = 1.0;
    for (int i = 0; i < 5; i++) {
      float keep = 1.0 - smoothstep(0.5, 1.0, fp * f);
      sum += amp * keep * vnoise(q * f + vec3(float(i) * 17.31, float(i) * -9.77, float(i) * 5.13));
      f *= 2.07;
      amp *= 0.68;
    }
    return sum;
  }

  // One turbulence layer: the pattern born age seconds ago, carried round
  // since at each radius's own Keplerian rate.
  float layerNoise(vec2 cs, float lx, float x, vec3 L, float dPsi, float dLx) {
    float theta = uOmegaIn * pow(uDiscIn / x, 1.5) * L.x;
    float c = cos(theta), s = sin(theta);
    vec2 r = vec2(c * cs.x + s * cs.y, -s * cs.x + c * cs.y);
    vec3 q = vec3(r * AZ_FREQ, lx * RAD_FREQ) + vec3(L.y, L.y * 1.37, L.y * 0.61);
    // Footprint: azimuth, the shear's azimuth change across the pixel's
    // radial extent (dθ/d ln r = -1.5 θ), and the radial extent itself.
    float fp = AZ_FREQ * (dPsi + 1.5 * abs(theta) * dLx) + RAD_FREQ * dLx;
    return discFbm(q, fp);
  }

  // Emission and opacity of one crossing of the disc plane.
  //   x: radius in r_s, cs: (cos, sin) of the azimuth in the disc frame,
  //   mu: |cos| between the photon and the disc normal, lz: the photon's
  //   angular momentum about the disc axis per unit energy (r_s),
  //   x0: camera radius (r_s), dPsi / dLx: pixel footprint, detail: noise on.
  vec4 discSample(float x, vec2 cs, float mu, float lz, float x0, float dPsi, float dLx, float detail) {
    float lx = log(x);
    float n = 0.0;
    float ring = 0.0;
    if (detail > 0.5) {
      float wA = uLayerA.z, wB = uLayerB.z;
      // A layer near its birth or death weighs nothing: skip its noise.
      float nA = wA > 0.01 ? layerNoise(cs, lx, x, uLayerA, dPsi, dLx) : 0.0;
      float nB = wB > 0.01 ? layerNoise(cs, lx, x, uLayerB, dPsi, dLx) : 0.0;
      n = (wA * nA + wB * nB) / sqrt(max(wA * wA + wB * wB, 1e-4));
      float fr = RING_FREQ * dLx;
      ring = vnoise(vec3(lx * RING_FREQ, 3.1, 7.7)) * (1.0 - smoothstep(0.5, 1.0, fr))
           + 0.5 * vnoise(vec3(lx * RING_FREQ * 2.3, 1.7, 2.9)) * (1.0 - smoothstep(0.5, 1.0, fr * 2.3));
    }
    // Radial envelope: the ISCO edge (the gas plunges inside it and its
    // temperature already falls to zero there) and a soft outer edge.
    float env = smoothstep(uDiscIn, uDiscIn + 0.25, x) * (1.0 - smoothstep(uDiscOut * 0.5, uDiscOut, x));
    float rho = env * exp(1.25 * n + 0.35 * ring - 0.35);

    // Novikov-Thorne / Shakura-Sunyaev temperature, peak 1 at r = 49/36 r_in.
    float F = pow(x, -0.75) * pow(max(1.0 - sqrt(uDiscIn / x), 0.0), 0.25) / F_MAX;
    float T = uDiscPeakK * F * (1.0 + 0.07 * n);

    // Frequency shift for a circular Keplerian orbit in Schwarzschild:
    // g = sqrt(1 - 3M/r) / (1 - Ω l_z), Ω = sqrt(M/r³), M = r_s / 2, and the
    // static camera's own blueshift 1/sqrt(1 - r_s/r0).
    float gFull = sqrt(max(1.0 - 1.5 / x, 1e-4)) / max(1.0 - lz * sqrt(0.5 / (x * x * x)), 1e-3)
                / sqrt(max(1.0 - 1.0 / x0, 1e-4));
    float g = exp(uDiscShift * log(gFull));

    float alpha = 1.0 - exp(-uDiscTau * rho / max(mu, 0.035));
    vec3 col = blackbody(g * T) * uDiscExposure;
    return vec4(col * alpha, alpha);
  }

  vec3 chromaScale(vec3 c, float k) {
    vec3 lab = _linSrgbToOklab(max(c, 0.0));
    lab.yz *= k;
    return max(_oklabToLinSrgb(lab), 0.0);
  }

  void main() {
    vec3 viewDir = normalize(vWorldPos - cameraPosition);
    vec3 camLocal = cameraPosition - uBhWorldPos;

    // Hole behind the camera: the underlying scene shows through.
    if (dot(-camLocal, viewDir) < 0.0) {
      discard;
    }

    float r0 = length(camLocal);
    vec3 eR = camLocal / r0;
    vec3 perp = viewDir - dot(viewDir, eR) * eR;
    float perpLen = length(perp);
    vec3 ePhi = perpLen > 1e-6 ? perp / perpLen : normalize(cross(eR, abs(eR.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));

    float v_r = dot(viewDir, eR);
    float v_phi = max(perpLen, 1e-6);

    // Impact parameter. The camera is a static observer at r0, so the
    // locally measured angle maps to b = r0 sin(a) / sqrt(1 - r_s / r0).
    float x0 = r0 / uRs;
    float redCam = sqrt(max(1.0 - 1.0 / x0, 1e-4));
    float bRs = x0 * v_phi / redCam;
    if (bRs > 400.0) {
      // Deflection 2/b < 0.005 rad: nothing to bend and no disc here.
      discard;
    }

    // Initial state at φ = 0. du/dφ = -u sqrt(1 - u) cot(a) for the static
    // observer's angle a.
    float u = 1.0 / x0;
    float du = -u * sqrt(max(1.0 - u, 0.0)) * v_r / v_phi;
    float phi = 0.0;

    // The disc plane in the orbital plane: height ∝ a cos φ + b sin φ =
    // A cos(φ - δ), zero at φ = δ + π/2 + kπ.
    float pa = dot(eR, uDiscN);
    float pb = dot(ePhi, uDiscN);
    float delta = atan(pb, pa);
    float sPrev = pa;
    // (cos φ, sin φ) carried by a fixed rotation per step, no trig in the loop.
    vec2 rot = vec2(1.0, 0.0);
#ifdef XR_MARCH
    const vec2 ROT_STEP = vec2(${Math.cos(MARCH_PHI / XR_MARCH_STEPS).toFixed(9)}, ${Math.sin(MARCH_PHI / XR_MARCH_STEPS).toFixed(9)});
#else
    const vec2 ROT_STEP = vec2(${Math.cos(MARCH_PHI / MARCH_STEPS).toFixed(9)}, ${Math.sin(MARCH_PHI / MARCH_STEPS).toFixed(9)});
#endif

    // Photon angular momentum about the disc axis per unit energy, r_s:
    // L = r x p for the photon arriving along -viewDir.
    float lz = dot(cross(camLocal, -viewDir), uDiscN) / (uRs * redCam);

    // Up to three crossings (the primary image, n = 1 and n = 2), recorded
    // during the march and shaded after it so screen derivatives of their
    // radius and azimuth are taken in uniform control flow.
    float cx0 = 0.0, cx1 = 0.0, cx2 = 0.0;      // radius, r_s (0 = none)
    vec2 cs0 = vec2(1.0, 0.0), cs1 = vec2(1.0, 0.0), cs2 = vec2(1.0, 0.0);
    float cm0 = 1.0, cm1 = 1.0, cm2 = 1.0;      // mu
    int nCross = 0;

    bool captured = perpLen <= 1e-6;

    for (int i = 0; i < N_STEPS; i++) {
      if (captured) break;
      float prevU = u;
      float prevDu = du;
      float prevPhi = phi;
      integrateRK4(u, du, DPHI);
      phi += DPHI;

      rot = vec2(rot.x * ROT_STEP.x - rot.y * ROT_STEP.y, rot.y * ROT_STEP.x + rot.x * ROT_STEP.y);
      float sCur = pa * rot.x + pb * rot.y;
      if (sPrev * sCur <= 0.0 && nCross < 3 && uDiscOn > 0.5) {
        float k = ceil((prevPhi - delta - 0.5 * PI) / PI);
        float phiC = delta + 0.5 * PI + k * PI;
        float t = clamp((phiC - prevPhi) / DPHI, 0.0, 1.0);
        // Cubic Hermite through both ends of the step.
        float t2 = t * t, t3 = t2 * t;
        float uc = (2.0 * t3 - 3.0 * t2 + 1.0) * prevU + (t3 - 2.0 * t2 + t) * DPHI * prevDu
                 + (-2.0 * t3 + 3.0 * t2) * u + (t3 - t2) * DPHI * du;
        float duc = (6.0 * t2 - 6.0 * t) / DPHI * prevU + (3.0 * t2 - 4.0 * t + 1.0) * prevDu
                  + (-6.0 * t2 + 6.0 * t) / DPHI * u + (3.0 * t2 - 2.0 * t) * du;
        float xc = 1.0 / max(uc, 1e-5);
        vec3 dirP = cos(phiC) * eR + sin(phiC) * ePhi;
        vec2 cs = vec2(dot(dirP, uDiscE1), dot(dirP, uDiscE2));
        float etn = pb * cos(phiC) - pa * sin(phiC);
        float mu = abs(uc * etn) / max(sqrt(duc * duc + uc * uc), 1e-6);
        if (uc > 0.0) {
          if (nCross == 0) { cx0 = xc; cs0 = cs; cm0 = mu; }
          else if (nCross == 1) { cx1 = xc; cs1 = cs; cm1 = mu; }
          else { cx2 = xc; cs2 = cs; cm2 = mu; }
          nCross++;
        }
      }
      sPrev = sCur;

      if (u >= 1.0) {
        captured = true;
        break;
      }

      if (u < 0.005 && du < 0.0 && i > 5) {
        float frac = (prevU - 0.005) / max(prevU - u, 1e-6);
        u   = mix(prevU,  u,  frac);
        du  = mix(prevDu, du, frac);
        phi = prevPhi + frac * DPHI;
        break;
      }
    }

    // Pixel footprints on the disc: ln r and azimuth changes across a pixel,
    // per crossing.
    vec3 lxs = log(max(vec3(cx0, cx1, cx2), vec3(1e-3)));
    vec3 dLx = max(abs(dFdx(lxs)), abs(dFdy(lxs)));
    vec3 dPsi = vec3(
      max(length(dFdx(cs0)), length(dFdy(cs0))),
      max(length(dFdx(cs1)), length(dFdy(cs1))),
      max(length(dFdx(cs2)), length(dFdy(cs2))));
    // A pixel that spans more than the whole disc radially sees only part
    // of that image (the sub-pixel n = 2 ring): weight it by its coverage.
    float lnSpan = log(uDiscOut / uDiscIn);
    vec3 cover = clamp(vec3(lnSpan) / max(dLx, vec3(1e-4)), 0.0, 1.0);

    vec3 disc = vec3(0.0);
    float trans = 1.0;
    if (cx0 >= uDiscIn && cx0 <= uDiscOut) {
      vec4 e = discSample(cx0, cs0, cm0, lz, x0, dPsi.x, dLx.x, 1.0);
      disc += trans * e.rgb * cover.x;
      trans *= 1.0 - e.a * cover.x;
    }
    if (cx1 >= uDiscIn && cx1 <= uDiscOut) {
      vec4 e = discSample(cx1, cs1, cm1, lz, x0, dPsi.y, dLx.y, 1.0);
      disc += trans * e.rgb * cover.y;
      trans *= 1.0 - e.a * cover.y;
    }
    if (cx2 >= uDiscIn && cx2 <= uDiscOut) {
      vec4 e = discSample(cx2, cs2, cm2, lz, x0, dPsi.z, dLx.z, 0.0);
      disc += trans * e.rgb * cover.z;
      trans *= 1.0 - e.a * cover.z;
    }
    // Soft shoulder on the disc's radiance above uKneeLo, to at most
    // uKneeHi, hue kept: the white-hot inner disc stays white-hot while the
    // post chain's bloom (which works on what exceeds ~0.9) stays a glow
    // round the disc instead of a fog over the shadow.
    float lum = dot(disc, vec3(0.2126, 0.7152, 0.0722));
    if (lum > uKneeLo) {
      float span = uKneeHi - uKneeLo;
      float over = lum - uKneeLo;
      disc *= (uKneeLo + over / (1.0 + over / span)) / lum;
    }
    if (uDiscChroma != 1.0) disc = chromaScale(disc, uDiscChroma);

    // The escaped ray's direction. Sampled outside any branch so texture
    // derivatives stay valid; captured rays weigh it by zero.
    float dr_dphi = -1.0 / max(u * u, 1e-8) * du;
    float rEnd = 1.0 / max(u, 1e-8);
    vec3 finalRadialDir = cos(phi) * eR + sin(phi) * ePhi;
    vec3 finalTangentialDir = -sin(phi) * eR + cos(phi) * ePhi;
    vec3 finalDir = normalize(dr_dphi * finalRadialDir + rEnd * finalTangentialDir + vec3(0.0, 1e-7, 0.0));
    // Rays still winding near the photon sphere when the march ends fade
    // to the shadow instead of meeting it in a hard ring.
    float trapWeight = smoothstep(0.05, 0.4, u);
    vec3 sky = (sampleSkybox(finalDir) + sampleBackdrop(finalDir)) * (1.0 - trapWeight) * (captured ? 0.0 : 1.0);

    // Lensing fades out with impact parameter (b is smooth on screen, so no
    // banding); by 300 r_s the deflection is too small to matter.
    float lensAlpha = 1.0 - smoothstep(50.0, 300.0, bRs);
    vec3 color = disc + trans * sky;
    gl_FragColor = vec4(color, lensAlpha * uOpacity);
  }
`;

function createGeodesicMesh(rs, opts = {}) {
  // Big enough that any normal viewing position keeps the camera inside the
  // sphere: with BackSide rendering the shader runs for every screen pixel
  // and there is no "sphere boundary" line where lensing stops.
  const sphereRadius = Math.max(rs * 5000, 100_000_000);

  const placeholderTex = new THREE.DataTexture(
    new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat
  );
  placeholderTex.needsUpdate = true;

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uSkybox: { value: opts.skyboxTexture || placeholderSkyCube() },
      uBlackbody: { value: opts.blackbody },
      uBhWorldPos: { value: new THREE.Vector3() },
      uRs: { value: rs },
      uOpacity: { value: 0.0 },
      uTime: { value: 0.0 },
      uDiscN: { value: new THREE.Vector3(0, 1, 0) },
      uDiscE1: { value: new THREE.Vector3(1, 0, 0) },
      uDiscE2: { value: new THREE.Vector3(0, 0, -1) },
      uDiscIn: { value: DISC.inner },
      uDiscOut: { value: DISC.outer },
      uDiscPeakK: { value: DISC.peakK },
      uDiscExposure: { value: 1 },
      uDiscTau: { value: DISC.tau },
      uDiscShift: { value: DISC.shift },
      uDiscChroma: { value: DISC.chroma },
      uDiscOn: { value: 1 },
      uKneeLo: { value: DISC.kneeLo },
      uKneeHi: { value: DISC.kneeHi },
      uOmegaIn: { value: (2 * Math.PI) / DISC.innerPeriodS },
      uLayerA: { value: new THREE.Vector3() },
      uLayerB: { value: new THREE.Vector3() },
      uBackdrop: { value: placeholderTex },
      uBackdropView: { value: new THREE.Matrix4() },
      uBackdropOn: { value: 0 },
    },
    vertexShader: /* glsl */`
      varying vec3 vWorldPos;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: opts.discOn ? DISC_FRAGMENT : LEGACY_FRAGMENT,
    transparent: true,
    blending: THREE.NormalBlending,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: true,
    toneMapped: true,
    ...(opts.discOn ? { extensions: { derivatives: true } } : {}),
  });

  const geometry = new THREE.SphereGeometry(sphereRadius, 64, 32);
  const mesh = new THREE.Mesh(geometry, material);
  return { mesh, material, geometry, sphereRadius };
}

function urlNumber(name) {
  try {
    const v = new URLSearchParams(window.location.search).get(name);
    return v !== null && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null;
  } catch (e) {
    return null;
  }
}

export function createBlackHole(opts = {}) {
  const rs = opts.visualRs || 80_000;
  // The disc is opt-in: ?bhdisc=1 or opts.disc (true or a parameter object).
  const discOn = !!opts.disc || urlNumber('bhdisc') === 1;
  const disc = { ...DISC, ...(typeof opts.disc === 'object' ? opts.disc : {}) };
  // ?bhdoppler=1 shows the fully physical beaming and colour shift.
  const urlShift = urlNumber('bhdoppler');
  if (urlShift !== null) disc.shift = Math.max(0, Math.min(1, urlShift));

  const blackbody = discOn ? createBlackbodyTexture() : null;

  const group = new THREE.Group();
  group.name = 'stellarBlackHole';

  const marcher = createGeodesicMesh(rs, { ...opts, blackbody, discOn });
  marcher.mesh.name = 'bhGeodesic';
  marcher.mesh.renderOrder = 100;
  group.add(marcher.mesh);

  const u = marcher.material.uniforms;
  const applyDisc = () => {
    if (!discOn) return;
    u.uDiscIn.value = disc.inner;
    u.uDiscOut.value = disc.outer;
    u.uDiscPeakK.value = disc.peakK;
    u.uDiscTau.value = disc.tau;
    u.uDiscShift.value = disc.shift;
    u.uDiscChroma.value = disc.chroma;
    u.uKneeLo.value = disc.kneeLo;
    u.uKneeHi.value = disc.kneeHi;
    u.uOmegaIn.value = (2 * Math.PI) / disc.innerPeriodS;
    // Exposure is set for the gas-frame peak: radiance there is `exposure`.
    u.uDiscExposure.value = disc.exposure / blackbody.userData.relLum(disc.peakK);
  };
  applyDisc();

  group.userData = {
    isBlackHole: true,
    name: 'Black Hole',
    type: 'Stellar-mass black hole',
    description:
      `Stellar-mass black hole, ~${STELLAR_BH.massSolar} solar masses. ` +
      `Schwarzschild radius ${STELLAR_BH.schwarzschildRadiusKm} km. ` +
      `Spin a/M = ${STELLAR_BH.spinParameter}.`,
    radius: rs * 30,
  };

  // The Milky Way behind the hole. The lensing sphere is drawn over the
  // scene's own galaxy, so the galaxy (opts.getBackdrop) is rendered from
  // the hole's centre into a small target every BACKDROP_EVERY seconds and
  // sampled by each escaped ray's bent direction: its image wraps into arcs
  // and an Einstein ring when it stands behind the shadow. The galaxy is 56M
  // units away and the camera within a few hundred thousand of the hole, so
  // the parallax between the two viewpoints is nothing.
  const BACKDROP_SIZE = 1024, BACKDROP_EVERY = 1.5;
  let backdrop = null;
  let backdropAge = Infinity;
  const bdTarget = new THREE.Vector3(), bdHole = new THREE.Vector3(), bdClear = new THREE.Color();
  function renderBackdrop(dt) {
    const obj = opts.getBackdrop && opts.getBackdrop();
    const renderer = opts.renderer;
    if (!obj || !renderer || !obj.visible) { u.uBackdropOn.value = 0; return; }
    backdropAge += dt;
    if (backdropAge < BACKDROP_EVERY) return;
    backdropAge = 0;
    if (!backdrop) {
      backdrop = {
        target: new THREE.WebGLRenderTarget(BACKDROP_SIZE, BACKDROP_SIZE, {
          type: THREE.HalfFloatType, depthBuffer: true,
          minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false,
        }),
        camera: new THREE.PerspectiveCamera(30, 1, 1, 10),
      };
    }
    obj.getWorldPosition(bdTarget);
    group.getWorldPosition(bdHole);
    const dist = bdHole.distanceTo(bdTarget);
    const reach = opts.backdropRadius || 16_000_000;
    if (dist < reach * 1.5) { u.uBackdropOn.value = 0; return; }
    const cam = backdrop.camera;
    cam.fov = THREE.MathUtils.radToDeg(2 * Math.atan(reach / dist));
    cam.near = Math.max(1, dist - reach * 2);
    cam.far = dist + reach * 2;
    cam.position.copy(bdHole);
    cam.lookAt(bdTarget);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld(true);
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevAlpha = renderer.getClearAlpha();
    renderer.getClearColor(bdClear);
    // A target render: never through the headset's cameras.
    const xrEnabled = renderer.xr ? renderer.xr.enabled : false;
    if (renderer.xr) renderer.xr.enabled = false;
    try {
      renderer.setRenderTarget(backdrop.target);
      renderer.setClearColor(0x000000, 0);
      renderer.autoClear = true;
      renderer.render(obj, cam);
      u.uBackdrop.value = backdrop.target.texture;
      u.uBackdropView.value.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      u.uBackdropOn.value = 1;
    } catch (e) {
      u.uBackdropOn.value = 0;
    } finally {
      renderer.setRenderTarget(prevTarget);
      renderer.setClearColor(bdClear, prevAlpha);
      renderer.autoClear = prevAutoClear;
      if (renderer.xr) renderer.xr.enabled = xrEnabled;
    }
  }

  // The disc's own clock: wall time, so it turns at the same pace whatever
  // the time presets do, clamped across hidden-tab gaps. Turbulence layer
  // ages are kept in doubles here and handed over as small numbers.
  const LAYER_LIFE = disc.innerPeriodS * 1.25;
  let discClock = 0;
  let lastNow = null;
  const nTmp = new THREE.Vector3(), e1Tmp = new THREE.Vector3(), qTmp = new THREE.Quaternion();

  function layer(out, offset) {
    const p = discClock / LAYER_LIFE + offset;
    const cycle = Math.floor(p);
    const f = p - cycle;
    // Seed per incarnation, kept small for the shader's hash precision.
    const seed = ((cycle * 0.6180339887 + offset * 0.37) % 1) * 97;
    out.set(f * LAYER_LIFE, seed, 1 - Math.abs(2 * f - 1));
  }

  const handle = {
    object: group,
    marcher,
    options: { rs },
    constants: STELLAR_BH,
    disc,

    setOpacity(value) {
      const v = Math.max(0, Math.min(1, value));
      u.uOpacity.value = v;
      marcher.mesh.visible = v > 0.001;
    },

    // 0 the film's symmetric disc, 1 the full Doppler and gravitational shift.
    setDopplerStrength(v) {
      disc.shift = Math.max(0, Math.min(1, v));
      applyDisc();
    },

    setDisc(params) {
      Object.assign(disc, params || {});
      applyDisc();
    },

    tick(galaxyTime) {
      const xr = !!(opts.renderer && opts.renderer.xr && opts.renderer.xr.isPresenting);
      const mat = marcher.mesh.material;
      if (mat && !!(mat.defines && mat.defines.XR_MARCH) !== xr) {
        mat.defines = { ...(mat.defines || {}) };
        if (xr) mat.defines.XR_MARCH = ''; else delete mat.defines.XR_MARCH;
        mat.needsUpdate = true;
      }
      u.uTime.value = galaxyTime;
      group.getWorldPosition(u.uBhWorldPos.value);

      const now = performance.now() / 1000;
      const dt = lastNow !== null ? Math.min(Math.max(now - lastNow, 0), 0.1) : 0;
      discClock += dt;
      lastNow = now;
      if (discOn && marcher.mesh.visible) renderBackdrop(dt);
      layer(u.uLayerA.value, 0);
      layer(u.uLayerB.value, 0.5);

      // Disc frame. With a backdrop the axis stands square to the line from
      // the hole to it, tipped tipDeg towards the far side, so a camera with
      // the galaxy behind the hole sees the disc nearly edge-on (the
      // Interstellar framing). Otherwise the group's local +Y.
      const bdObj = discOn && opts.getBackdrop && opts.getBackdrop();
      if (bdObj && Number.isFinite(disc.tipDeg)) {
        group.getWorldPosition(e1Tmp);
        bdObj.getWorldPosition(nTmp);
        const from = e1Tmp.sub(nTmp).normalize();
        nTmp.set(0, 1, 0).addScaledVector(from, -from.y).normalize();
        const tip = THREE.MathUtils.degToRad(disc.tipDeg);
        nTmp.multiplyScalar(Math.cos(tip)).addScaledVector(from, Math.sin(tip)).normalize();
        e1Tmp.set(1, 0, 0).addScaledVector(nTmp, -nTmp.x).normalize();
      } else {
        group.getWorldQuaternion(qTmp);
        nTmp.set(0, 1, 0).applyQuaternion(qTmp).normalize();
        e1Tmp.set(1, 0, 0).applyQuaternion(qTmp).normalize();
      }
      u.uDiscN.value.copy(nTmp);
      u.uDiscE1.value.copy(e1Tmp);
      u.uDiscE2.value.crossVectors(nTmp, e1Tmp).normalize();

      // Skybox loads asynchronously: pick it up once available, with
      // anisotropic filtering so stretched stars near the silhouette stay
      // clean.
      if (opts.getSkyboxTexture) {
        const tex = opts.getSkyboxTexture();
        if (tex && u.uSkybox.value !== tex) {
          u.uSkybox.value = tex;
          if (opts.renderer && tex.anisotropy < 4) {
            tex.anisotropy = opts.renderer.capabilities.getMaxAnisotropy();
            tex.needsUpdate = true;
          }
        }
      }
    },

    dispose() {
      marcher.geometry.dispose();
      marcher.material.dispose();
      if (blackbody) blackbody.dispose();
      if (backdrop) backdrop.target.dispose();
    },
  };
  // Dev handle for tuning from the console and for the capture scripts.
  try {
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) window.__blackHole = handle;
  } catch (e) { /* no window */ }
  return handle;
}
